import type { GetChatAttachmentResponseDTO, UploadChatAttachmentResponseDTO } from '@shared/schemas/chat-attachment';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    details?: Record<string, unknown>;
    message?: string;
  };
  success?: false;
};

function createApiUrl(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const url = new URL(path, apiBaseUrl);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as (T & ApiErrorPayload) | ApiErrorPayload | null;

  if (!response.ok || payload === null) {
    const fallbackMessage = response.statusText || 'La requête a échoué.';
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? fallbackMessage,
      status: response.status,
    });
  }

  return payload as T;
}

export async function uploadChatAttachment(file: File, conversationId?: string) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(createApiUrl('/api/chat/attachments', { conversationId }), {
    body: formData,
    credentials: 'include',
    method: 'POST',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<UploadChatAttachmentResponseDTO>>(response);
  return payload.data;
}

export async function getChatAttachment(attachmentId: string) {
  const response = await fetch(createApiUrl(`/api/chat/attachments/${attachmentId}`), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<GetChatAttachmentResponseDTO>>(response);
  return payload.data;
}
